import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CMOption } from '../../models/cm-option.interface';

interface CmMultiselectOption {
	display: string;
	value: string | number;
	selected: boolean;
}

@UntilDestroy()
@Component({
    selector: 'cm-multiselect',
    templateUrl: './cm-multiselect.component.html',
    styleUrls: ['./cm-multiselect.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => CmMultiselectComponent) }],
    standalone: false
})
export class CmMultiselectComponent implements ControlValueAccessor, OnChanges {
	@Input() options: CMOption[] = [];
	@Input() searchable: boolean = true;
	@Input() localizationPrefix: string = 'Core/System.SelectionList';
	@Input() viewOnly: boolean = false;
	search: UntypedFormControl;

	filteredOptions: CmMultiselectOption[] = [];
	initialized: boolean = false;
	touched = false;
	disabled = false;

	private selected: (string | number)[] = [];
	private searchValue: string = '';

	constructor(private readonly cd: ChangeDetectorRef) {
		this.search = new UntypedFormControl('');

		this.search.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
			this.searchValue = value;
			this.updateDisplay();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.options && !changes.options.firstChange) {
			this.initialized = true;
			this.updateDisplay();
		}
	}

	onChange = (selected: (string | number)[]): any => {};
	onTouched = () => {};

	registerOnChange(onChange: any): void {
		this.onChange = onChange;
	}

	registerOnTouched(onTouched: any): void {
		this.onTouched = onTouched;
	}

	writeValue(value: (string | number)[]): void {
		this.selected = [...value];
		this.updateDisplay();
	}

	onSelect(value: string | number): void {
		if (this.selected.includes(value)) {
			this.selected = this.selected.filter(opt => opt !== value);
		} else {
			this.selected = [...this.selected, value];
		}
		this.updateDisplay();
	}

	onSelectAll() {
		this.onToggleSelection('select');
	}

	onUnselectAll() {
		this.onToggleSelection('unselect');
	}

	markAsTouched(): void {
		if (!this.touched) {
			this.onTouched();
			this.touched = true;
		}
	}

	setDisabledState(disabled: boolean): void {
		this.disabled = disabled;
	}

	private onToggleSelection(state: 'unselect' | 'select'): void {
		const visibleOptions = this.filteredOptions.map(opt => opt.value);
		const selectedInvisibleOptions = this.selected.filter(option => !visibleOptions.includes(option));
		if (state === 'select') {
			this.selected = [...visibleOptions, ...selectedInvisibleOptions];
		} else {
			this.selected = [...selectedInvisibleOptions];
		}
		this.updateDisplay();
	}

	private updateDisplay() {
		this.filteredOptions = (this.options || [])
			.filter(opt => !this.searchValue || opt.display.toLocaleLowerCase().includes(this.searchValue))
			.map(opt => ({
				...opt,
				selected: this.selected.includes(opt.value),
			}));
		this.cd.detectChanges();
		this.onChange([...this.selected]);
	}
}
